/**
 * detokenize.ts (SCRUM-1022, Epic SCRUM-723)
 *
 * The NLQ snapshot sent to watsonx carries customers only as opaque tokens
 * `CUST_<portfolioId>` — never real names (that is the PII contract: the model
 * never sees customer names). The model reproduces those tokens in its answer,
 * and we restore the real name HERE, client-side, immediately before the answer
 * is shown/stored.
 *
 * Fail-open by design: an unknown token, or a portfolio id whose name is empty
 * (co-holder rows — the API coalesces a null name to ""), is left verbatim.
 * We never throw and never render a blank where a token was.
 */

/** Matches a customer token: CUST_ followed by the numeric portfolio id. */
const CUSTOMER_TOKEN = /\bCUST_(\d+)\b/g;

/**
 * Replace every `CUST_<portfolioId>` token in `text` with the real customer
 * name from `namesByPortfolioId`. Unknown tokens and empty names are left as-is.
 *
 * @param text  answer markdown / followup / source line that may contain tokens
 * @param namesByPortfolioId  map of portfolioId -> real name (from /api/customers)
 */
export function detokenizeCustomerNames(
  text: string,
  namesByPortfolioId: ReadonlyMap<string, string>,
): string {
  if (!text) return text;
  return text.replace(CUSTOMER_TOKEN, (token, portfolioId: string) => {
    const name = namesByPortfolioId.get(portfolioId);
    // Fail-open: leave the token untouched when there is no usable name.
    return name && name.trim().length > 0 ? name : token;
  });
}

/** Build the portfolioId -> name lookup from the cached customer dataset. */
export function buildCustomerNameMap(
  customers: readonly { portfolioId: string; name: string }[],
): Map<string, string> {
  const map = new Map<string, string>();
  for (const c of customers) {
    if (c.portfolioId) map.set(c.portfolioId, c.name);
  }
  return map;
}
